import type { FormatoPublicacion } from '@app/shared';
import { PUBLICACION_ARCHIVO_MAX_BYTES } from '@app/shared';
import { ArrowDown, ArrowUp, FileVideo, Upload, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type DragEvent, type ReactNode } from 'react';

import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';

// Elegir el archivo (o las imágenes del carrusel, en orden). Ailen no tipea
// nombres: el archivo se sube tal cual y el backend lo nombra (R2, opción B).

const ACEPTA: Record<FormatoPublicacion, string> = {
  REEL: 'video/*',
  HISTORIA: 'image/*,video/*',
  CARRUSEL: 'image/*',
};

function compatible(formato: FormatoPublicacion, f: File): boolean {
  const video = f.type.startsWith('video/');
  const imagen = f.type.startsWith('image/');
  if (formato === 'REEL') return video;
  if (formato === 'CARRUSEL') return imagen;
  return video || imagen;
}

function mb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function SelectorArchivos({
  formato,
  archivos,
  onChange,
  compacto = false,
}: {
  formato: FormatoPublicacion;
  archivos: File[];
  onChange: (archivos: File[]) => void;
  // Una sola linea de alto: para que el formulario entre en una pantalla.
  compacto?: boolean;
}): JSX.Element {
  const es = useI18n();
  const t = es.publicar.archivo;
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const multiple = formato === 'CARRUSEL';

  const agregar = (lista: FileList | null): void => {
    if (!lista) return;
    const nuevos: File[] = [];
    let error: string | null = null;
    for (const f of Array.from(lista)) {
      if (!compatible(formato, f)) error = t.tipoInvalido;
      else if (f.size > PUBLICACION_ARCHIVO_MAX_BYTES) error = t.muyGrande;
      else nuevos.push(f);
    }
    setAviso(error);
    onChange(multiple ? [...archivos, ...nuevos] : nuevos.slice(0, 1));
  };

  const soltar = (e: DragEvent): void => {
    e.preventDefault();
    setArrastrando(false);
    agregar(e.dataTransfer.files);
  };

  const mover = (i: number, delta: number): void => {
    const j = i + delta;
    if (j < 0 || j >= archivos.length) return;
    const copia = [...archivos];
    const a = copia[i]!;
    copia[i] = copia[j]!;
    copia[j] = a;
    onChange(copia);
  };

  const ayuda = formato === 'REEL' ? t.ayudaReel : formato === 'CARRUSEL' ? t.ayudaCarrusel : t.ayudaHistoria;

  return (
    <div className="flex flex-col gap-2">
      {!compacto && (
        <span className="text-sm font-medium text-marca-gris">
          {multiple ? t.etiquetaCarrusel : t.etiqueta}
        </span>
      )}
      <button
        type="button"
        onClick={() => input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={soltar}
        className={cn(
          'flex items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 text-center transition-colors',
          compacto ? 'flex-row py-3' : 'flex-col py-8',
          arrastrando
            ? 'border-marca-primario bg-surface-primario'
            : 'border-border-strong bg-surface hover:border-marca-primario/60',
        )}
        aria-describedby={`${id}-ayuda`}
      >
        <Upload className="shrink-0 text-marca-primario" size={compacto ? 18 : 28} />
        <span className="text-sm font-medium text-text-strong">{t.arrastrar}</span>
        <span id={`${id}-ayuda`} className="text-xs text-text-subtle">
          {ayuda}
        </span>
      </button>
      <input
        ref={input}
        id={id}
        type="file"
        accept={ACEPTA[formato]}
        multiple={multiple}
        className="hidden"
        data-testid="input-archivos"
        onChange={(e) => {
          agregar(e.target.files);
          e.target.value = '';
        }}
      />
      {aviso && (
        <span role="alert" className="text-xs text-danger">
          {aviso}
        </span>
      )}
      {archivos.length > 0 && (
        <ul className="flex flex-col gap-1">
          {archivos.map((f, i) => (
            <li
              key={`${f.name}-${String(i)}`}
              className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface px-2 py-1.5 text-sm"
            >
              {multiple && (
                <span className="w-6 text-center font-mono text-xs text-text-faint">
                  {String(i + 1).padStart(2, '0')}
                </span>
              )}
              {f.type.startsWith('video/') ? (
                <FileVideo size={16} className="text-text-faint" />
              ) : (
                <VistaPrevia archivo={f} />
              )}
              <span className="truncate text-text-strong">{f.name}</span>
              <span className="ml-auto shrink-0 text-xs text-text-faint">{mb(f.size)}</span>
              {multiple && (
                <>
                  <BotonChico onClick={() => mover(i, -1)} disabled={i === 0} title={t.subir}>
                    <ArrowUp size={14} />
                  </BotonChico>
                  <BotonChico
                    onClick={() => mover(i, 1)}
                    disabled={i === archivos.length - 1}
                    title={t.bajar}
                  >
                    <ArrowDown size={14} />
                  </BotonChico>
                </>
              )}
              <BotonChico onClick={() => onChange(archivos.filter((_, k) => k !== i))} title={t.quitar}>
                <X size={14} />
              </BotonChico>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VistaPrevia({ archivo }: { archivo: File }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return undefined;
    const u = URL.createObjectURL(archivo);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [archivo]);
  return url ? (
    <img src={url} alt="" className="h-8 w-8 rounded object-cover" />
  ) : (
    <span className="h-8 w-8 rounded bg-surface-muted" />
  );
}

function BotonChico({
  children,
  onClick,
  disabled,
  title,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className="rounded p-1 text-text-faint hover:bg-surface-muted hover:text-text-strong disabled:opacity-30"
    >
      {children}
    </button>
  );
}
